/**
 * Pure date-range math for the unified orders-history filters. Presets are
 * computed in the browser's local timezone on purpose: a remote owner's
 * "Hoy" is their day. `to` is EXCLUSIVE (`created_at < to`); a null `to`
 * means "now" and is resolved to a concrete instant at fetch time (frozen
 * per fetch generation so load-more OFFSETs don't drift with live orders).
 */

export type THistoryDatePreset = 'today' | '7d' | '30d' | '90d' | 'custom';

export interface IHistoryDateRange {
  preset: THistoryDatePreset;
  from: Date;
  /** Exclusive upper bound; null = "now" (resolved at fetch time). */
  to: Date | null;
}

export const DEFAULT_HISTORY_DATE_PRESET: Exclude<
  THistoryDatePreset,
  'custom'
> = '30d';

const PRESET_DAYS: Record<Exclude<THistoryDatePreset, 'custom'>, number> = {
  today: 1,
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** "Últimos N días" including today: from = local midnight N-1 days ago. */
export function getPresetRange(
  preset: Exclude<THistoryDatePreset, 'custom'>,
  now: Date = new Date()
): IHistoryDateRange {
  const from = startOfLocalDay(now);
  from.setDate(from.getDate() - (PRESET_DAYS[preset] - 1));
  return { preset, from, to: null };
}

/** Parses a native `<input type="date">` value as a LOCAL day. */
export function parseLocalDayInput(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) return null;
  const date = new Date(+match[1], +match[2] - 1, +match[3]);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Custom range from two date-input values. Both required; swapped bounds are
 * normalized (same convention as the price popover); `to` becomes the NEXT
 * local midnight so the selected end day is included under the exclusive
 * comparison.
 */
export function getCustomRange(
  fromInput: string,
  toInput: string
): IHistoryDateRange | null {
  const from = parseLocalDayInput(fromInput);
  const to = parseLocalDayInput(toInput);
  if (!from || !to) return null;

  const [lower, upper] = from <= to ? [from, to] : [to, from];
  const exclusiveTo = new Date(upper);
  exclusiveTo.setDate(exclusiveTo.getDate() + 1);
  return { preset: 'custom', from: lower, to: exclusiveTo };
}

/** Formats a Date as a native date-input value (local day). */
export function toDateInputValue(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
